/* Shared background music URLs & playlists for all character pages.
   Served by FastAPI StaticFiles mounted at /api/static/.

   All tracks © Kevin MacLeod (incompetech.com) — CC-BY 3.0
   https://creativecommons.org/licenses/by/3.0/
   Credit required: "Music: www.incompetech.com, Kevin MacLeod" in Footer.

   Each character has a PLAYLIST of up to 6 tracks + Silence (added by MusicPicker):
     - classique   (original royalty-free, kept from day one)
     - hit         (loud rhythmic Celtic signature)
     - emouvante   (emotional, heart-opening)
     - medieval    (shared Minstrel Guild hurdy-gurdy)
     - immersive   (shared Clash Defiant wall-of-sound)
*/
const API_ROOT = process.env.REACT_APP_BACKEND_URL;
const A = (f) => `${API_ROOT}/api/static/audio/${f}`;

// Shared "theme" tracks used across all character playlists
const MEDIEVAL  = { id: "medieval",  url: A("medieval_hurdy.mp3"),  label: "Médiéval · Minstrel Guild" };
const IMMERSIVE = { id: "immersive", url: A("immersive_wall.mp3"),  label: "Épique Immersif · Clash Defiant" };

// Per-character 5-track playlist (dropdown order top→bottom)
export const PLAYLISTS = {
  barde: [
    { id: "classique", url: A("celtic_impulse.mp3"),  label: "Classique · Celtic Impulse" },
    { id: "hit",       url: A("barde_hit.mp3"),       label: "Hit · Fiddles McGinty (gigue)" },
    { id: "emouvante", url: A("barde_emouvante.mp3"), label: "Émouvante · Fairytale Waltz" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  druide: [
    { id: "classique", url: A("meditation_impromptu.mp3"), label: "Classique · Meditation Impromptu" },
    { id: "hit",       url: A("druide_hit.mp3"),           label: "Hit · Skye Cuillin (ethereal)" },
    { id: "emouvante", url: A("druide_emouvante.mp3"),     label: "Émouvante · Mesmerize (profond)" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  vate: [
    { id: "classique", url: A("om_tibetan.mp3"),      label: "Classique · Om Tibétain 136 Hz" },
    { id: "fond",      url: A("enchanted_journey.mp3"), label: "Nature sonore · Enchanted Journey" },
    { id: "hit",       url: A("vate_hit.mp3"),        label: "Hit · Night of the Owl" },
    { id: "emouvante", url: A("vate_emouvante.mp3"),  label: "Émouvante · Reawakening" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  farfadet: [
    { id: "classique", url: A("monkeys_clown.mp3"),     label: "Classique · Monkeys Spinning Monkeys" },
    { id: "hit",       url: A("farfadet_hit.mp3"),      label: "Hit · Scheming Weasel (faster)" },
    { id: "emouvante", url: A("farfadet_emouvante.mp3"), label: "Émouvante · Pleasant Porridge" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  grammarien: [
    { id: "classique", url: A("gregorian_kyrie.mp3"),       label: "Classique · Gregorian Kyrie" },
    { id: "hit",       url: A("grammarien_hit.mp3"),        label: "Hit · Virtutes Instrumenti" },
    { id: "emouvante", url: A("grammarien_emouvante.mp3"),  label: "Émouvante · Angevin (danse)" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  korrigan: [
    { id: "classique", url: A("lord_of_the_land.mp3"),  label: "Classique · Lord of the Land" },
    { id: "hit",       url: A("korrigan_hit.mp3"),      label: "Hit · Pippin the Hunchback" },
    { id: "emouvante", url: A("korrigan_emouvante.mp3"), label: "Émouvante · The Pyre" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  gaia: [
    { id: "classique", url: A("enchanted_journey.mp3"), label: "Classique · Enchanted Journey" },
    { id: "hit",       url: A("gaia_hit.mp3"),          label: "Hit · Ascending the Vale" },
    { id: "emouvante", url: A("gaia_emouvante.mp3"),    label: "Émouvante · Lightless Dawn" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  ahura: [
    { id: "classique", url: A("ahura_mazda_fire.mp3"), label: "Classique · Hymne du Feu Sacré" },
    { id: "hit",       url: A("grammarien_hit.mp3"),   label: "Hit · Virtutes Instrumenti" },
    { id: "emouvante", url: A("gaia_hit.mp3"),         label: "Émouvante · Ascending the Vale" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  dagda: [
    { id: "classique", url: A("dagda_harp.mp3"),       label: "Classique · Les 3 Airs de la Harpe" },
    { id: "hit",       url: A("barde_hit.mp3"),        label: "Hit · Fiddles McGinty (gigue)" },
    { id: "emouvante", url: A("barde_emouvante.mp3"),  label: "Émouvante · Fairytale Waltz" },
    MEDIEVAL,
    IMMERSIVE,
  ],
  // Greek pantheon + Urantia (Formation · Amis des Celtes)
  urantia: [
    { id: "classique", url: A("gregorian_kyrie.mp3"),       label: "Classique · Gregorian Kyrie" },
    { id: "cosmique",  url: A("meditation_impromptu.mp3"),  label: "Cosmique · Meditation Impromptu" },
    { id: "lumiere",   url: A("vate_emouvante.mp3"),        label: "Lumière · Reawakening" },
    { id: "chant",     url: A("ahura_mazda_fire.mp3"),      label: "Chant · Hymne lumineux" },
    IMMERSIVE,
  ],
  zeus: [
    { id: "classique", url: A("immersive_wall.mp3"),   label: "Classique · Clash Defiant (foudroyant)" },
    { id: "foudre",    url: A("korrigan_hit.mp3"),     label: "Foudre · Pippin the Hunchback" },
    { id: "olympe",    url: A("grammarien_hit.mp3"),   label: "Olympe · Virtutes Instrumenti" },
    { id: "aigle",     url: A("druide_hit.mp3"),       label: "Aigle · Skye Cuillin" },
    MEDIEVAL,
  ],
  apollon: [
    { id: "classique", url: A("gaia_emouvante.mp3"),   label: "Classique · Lightless Dawn (lyre)" },
    { id: "soleil",    url: A("barde_emouvante.mp3"),  label: "Soleil · Fairytale Waltz" },
    { id: "muse",      url: A("druide_emouvante.mp3"), label: "Muse · Mesmerize" },
    { id: "lyre",      url: A("ahura_mazda_fire.mp3"), label: "Lyre · Hymne lumineux" },
    IMMERSIVE,
  ],
  eros: [
    { id: "classique", url: A("barde_emouvante.mp3"),  label: "Classique · Fairytale Waltz" },
    { id: "tendre",    url: A("druide_emouvante.mp3"), label: "Tendre · Mesmerize" },
    { id: "psyche",    url: A("vate_emouvante.mp3"),   label: "Psyché · Reawakening" },
    { id: "miel",      url: A("gaia_emouvante.mp3"),   label: "Miel · Lightless Dawn" },
    MEDIEVAL,
  ],
  phanes: [
    { id: "classique", url: A("phanes_lumiere.mp3"),   label: "Classique · Aube de Phanès (lumineux)" },
    { id: "aurore",    url: A("vate_emouvante.mp3"),   label: "Aurore · Reawakening" },
    { id: "cosmos",    url: A("om_tibetan.mp3"),       label: "Cosmos · Om 136 Hz" },
    { id: "or",        url: A("ahura_mazda_fire.mp3"), label: "Or · Hymne lumineux" },
    IMMERSIVE,
  ],
};

// Legacy single-track mapping — still used by pages that haven't switched to MusicPicker yet
export const TRACKS = {
  barde:      { url: A("celtic_impulse.mp3"),      label: "Celtic Impulse",       accent: "#f5d572" },
  druide:     { url: A("meditation_impromptu.mp3"), label: "Meditation Impromptu", accent: "#a67bff" },
  vate:       { url: A("om_tibetan.mp3"),          label: "Om Tibétain 136 Hz",   accent: "#f0d97a" },
  gaia:       { url: A("enchanted_journey.mp3"),   label: "Enchanted Journey",    accent: "#7bc47f" },
  farfadet:   { url: A("monkeys_clown.mp3"),       label: "Monkeys Spinning",     accent: "#c8f76c" },
  grammarien: { url: A("gregorian_kyrie.mp3"),     label: "Gregorian Kyrie",      accent: "#e6d9a2" },
  korrigan:   { url: A("lord_of_the_land.mp3"),    label: "Lord of the Land",     accent: "#e07a4f" },
  suantrai:   { url: A("suantrai_berceuse.mp3"),   label: "Berceuse envoûtante",  accent: "#8fb3d4" },
};

// Suantraí sleep page — multi-track rotation enriched with new Celtic tracks
export const SUANTRAI_TRACKS = [
  { id: "berceuse",   url: A("suantrai_berceuse.mp3"),  label: "Berceuse envoûtante · 65 Hz" },
  { id: "om",         url: A("om_tibetan.mp3"),         label: "Om Tibétain · 136 Hz" },
  { id: "skye",       url: A("druide_hit.mp3"),         label: "Skye Cuillin (celtic)" },
  { id: "fairytale",  url: A("barde_emouvante.mp3"),    label: "Fairytale Waltz" },
  { id: "reawakening", url: A("vate_emouvante.mp3"),    label: "Reawakening" },
  { id: "meditation", url: A("meditation_impromptu.mp3"), label: "Meditation Impromptu" },
  { id: "gregorian",  url: A("gregorian_kyrie.mp3"),    label: "Gregorian Kyrie" },
];

// Per-character accent colors (reused by MusicPicker)
export const ACCENTS = {
  barde: "#f5d572",
  druide: "#a67bff",
  vate: "#f0d97a",
  gaia: "#7bc47f",
  farfadet: "#c8f76c",
  grammarien: "#e6d9a2",
  korrigan: "#e07a4f",
  ahura: "#f5b800",
  dagda: "#c48a3f",
  urantia: "#64a0ff",   // bleu cosmique (Fascicules d'Urantia)
  zeus: "#ffd84a",      // or foudre
  apollon: "#ff9a4a",   // or soleil
  eros: "#ff6b9d",      // rose Aphrodite
  phanes: "#ffe9a8",    // or-aube primordial
  delphe: "#ffcf5a",    // or solaire Pythien
};
